import { FormGroup } from '@angular/forms';
import { buildUnitsGridRawRows } from './units-grid-input-row-ops.helper';
import {
  UnitsGridColumn,
  UnitsGridCommitEvent,
  UnitsGridCommitMode,
  UnitsGridCommitReason
} from './units-grid-input.models';

export interface UnitsGridCommitCoordinatorConfig {
  getMode: () => UnitsGridCommitMode;
  getDebounceMs: () => number;
  getRows: () => FormGroup[];
  getColumns: () => readonly UnitsGridColumn[];
  shouldBlockWhenInvalid: () => boolean;
  emit: (event: UnitsGridCommitEvent) => void;
}

export function isUnitsGridRowCompletelyBlank(
  row: FormGroup,
  columns: readonly UnitsGridColumn[],
  includeDisabled = false
): boolean {
  const editableControls = columns
    .filter((column) => column.editable !== false)
    .map((column) => row.get(column.field))
    .filter((control) => !!control && (includeDisabled || control.enabled));

  if (editableControls.length === 0) {
    return true;
  }

  const isBlank = (control: typeof editableControls[number]): boolean => {
    const value = control?.value;
    return value === null
      || value === undefined
      || value === ''
      || (typeof value === 'number' && Number.isNaN(value));
  };

  if (editableControls.every(isBlank)) {
    return true;
  }

  // Legacy grids use zero as the first value in their blank input row.
  return editableControls[0]?.value === 0 && editableControls.slice(1).every(isBlank);
}

export class UnitsGridCommitCoordinator {
  private debounceHandle: ReturnType<typeof setTimeout> | null = null;
  private lastCommittedSignature = '';

  constructor(private readonly config: UnitsGridCommitCoordinatorConfig) {}

  public destroy(): void {
    if (this.debounceHandle) {
      clearTimeout(this.debounceHandle);
      this.debounceHandle = null;
    }
  }

  public requestCommit(reason: UnitsGridCommitReason, forceImmediate = false): void {
    const mode = this.config.getMode();
    if (mode === 'manual') {
      return;
    }

    if (this.debounceHandle) {
      clearTimeout(this.debounceHandle);
      this.debounceHandle = null;
    }

    const shouldDebounce = !forceImmediate && mode === 'debounced';
    if (shouldDebounce) {
      this.debounceHandle = setTimeout(() => {
        this.debounceHandle = null;
        this.emitCommit(reason);
      }, Math.max(0, this.config.getDebounceMs()));
      return;
    }

    this.emitCommit(reason);
  }

  private emitCommit(reason: UnitsGridCommitReason): void {
    const rows = this.config.getRows();
    const columns = this.config.getColumns();
    if (this.config.shouldBlockWhenInvalid() && this.hasBlockingValidationErrors(rows, columns)) {
      return;
    }

    const rawRows = buildUnitsGridRawRows(rows);
    const signature = JSON.stringify(rawRows);
    if (signature === this.lastCommittedSignature) {
      return;
    }

    this.lastCommittedSignature = signature;
    this.config.emit({ reason, rows: rawRows, signature });
  }

  private hasBlockingValidationErrors(rows: FormGroup[], columns: readonly UnitsGridColumn[]): boolean {
    return rows.some((row) => {
      if (isUnitsGridRowCompletelyBlank(row, columns)) {
        return false;
      }

      return columns.some((column) => {
        const control = row.get(column.field);
        return !!control && control.invalid;
      });
    });
  }
}
